import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Rundown, RundownItem, Story, Participant } from "../../common/types/domain";
import { useTags } from "../../common/contexts/TagsContext";
const DataColumn = React.lazy(() =>
  import("../../logger/components/DataColumn").then((m) => ({ default: m.DataColumn })),
);
import { StatsWidget } from "../components/StatsWidget";
import { StreamFeed } from "./StreamFeed";
import { ChatSidebar } from "../../common/components/ChatSidebar";
import { useTimecode } from "../../../hooks/useTimecode";
import { socketService } from "../../../services/socket";
import { CustomDialog } from "../../common/components/CustomDialog";
import { StarRating } from "@/components/ui/star-rating";
import { recalculateRundownTimes, addTimes, adjustTime } from "../../../utils/timeUtils";

const TimeControl: React.FC<{ value: string; onChange: (val: string) => void }> = ({
  value,
  onChange,
}) => {
  const safeValue = value || "00:00:00";
  const parts = safeValue.split(":");

  const ArrowBtn = ({ dir, onClick }: { dir: "up" | "down"; onClick: () => void }) => (
    <button
      onClick={onClick}
      style={{
        background: "transparent",
        border: "none",
        color: "var(--color-text-muted)",
        cursor: "pointer",
        padding: "2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        transition: "color 0.2s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-accent-danger)")}
      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-text-muted)")}
    >
      <svg
        width="10"
        height="6"
        viewBox="0 0 10 6"
        fill="currentColor"
        style={{ display: "block" }}
      >
        {dir === "up" ? <path d="M5 0L10 6H0L5 0Z" /> : <path d="M5 6L0 0H10L5 6Z" />}
      </svg>
    </button>
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: "var(--color-input-bg)",
        padding: "4px 8px",
        borderRadius: "var(--border-radius-sm)",
        border: "1px solid var(--color-panel-border)",
        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.5)",
      }}
    >
      {["h", "m", "s"].map((unit, i) => (
        <React.Fragment key={unit}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "32px",
            }}
          >
            <ArrowBtn dir="up" onClick={() => onChange(adjustTime(safeValue, 1, unit as any))} />
            <span
              style={{
                fontFamily: "var(--font-mono)",
                color: "var(--color-text-primary)",
                fontSize: "1rem",
                fontWeight: "700",
                marginTop: "2px",
                marginBottom: "2px",
              }}
            >
              {parts[i] || "00"}
            </span>
            <ArrowBtn dir="down" onClick={() => onChange(adjustTime(safeValue, -1, unit as any))} />
          </div>
          {i < 2 && (
            <span
              style={{ color: "var(--color-panel-border)", fontSize: "1rem", fontWeight: "bold" }}
            >
              :
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

interface DashboardProps {
  externalActiveRundown?: Rundown | null;
  externalAllRundowns?: Rundown[];
  externalStories?: Story[];
  externalParticipants?: Participant[];
  externalActiveLoggers?: { id: string; name: string }[];
  externalStaff?: any[];
  isConnected: boolean;
  eventsCount: number;
  transcriptsCount: number;
  onFetchStoryLogs?: (storyId: string) => void;
  isLoading?: boolean;
}

export const Dashboard: React.FC<DashboardProps> = ({
  externalActiveRundown,
  externalAllRundowns,
  externalStories,
  externalParticipants,
  externalActiveLoggers,
  externalStaff,
  isConnected,
  eventsCount,
  transcriptsCount,
  onFetchStoryLogs,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { getTagLabel, getTagColor, getCameraName } = useTags();

  const [hoveredRundownItemId, setHoveredRundownItemId] = useState<string | null>(null);
  const [confirmGoLiveItem, setConfirmGoLiveItem] = useState<RundownItem | null>(null);
  const [focusLogger, setFocusLogger] = useState<string | null>(null);

  // Rundown Selection & Creation State
  const [showRundownSelector, setShowRundownSelector] = useState(false);
  const [confirmSwitchRundown, setConfirmSwitchRundown] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventDescription, setNewEventDescription] = useState("");
  const [newEventTime, setNewEventTime] = useState("");
  const [newEventDuration, setNewEventDuration] = useState("00:05:00");
  const [newEventParallel, setNewEventParallel] = useState(false);

  // Drag & Drop State
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);

  const rundownDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        rundownDropdownRef.current &&
        !rundownDropdownRef.current.contains(event.target as Node)
      ) {
        setShowRundownSelector(false);
      }
    };

    if (showRundownSelector) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showRundownSelector]);

  useEffect(() => {
    if (
      isCreatingEvent &&
      !editingEventId &&
      externalActiveRundown &&
      externalActiveRundown.items.length > 0
    ) {
      const last = externalActiveRundown.items[externalActiveRundown.items.length - 1];
      if (newEventParallel) {
        setNewEventTime(last.plannedTime || "00:00:00");
      } else {
        setNewEventTime(addTimes(last.plannedTime || "00:00:00", last.duration || "00:00:00"));
      }
    }
  }, [isCreatingEvent, editingEventId, newEventParallel, externalActiveRundown]);

  // UI Helpers
  const handleActivateRundown = (id: string, name: string) => {
    if (!socketService.connected) return;
    setConfirmSwitchRundown({ id, name });
  };

  const executeSwitchRundown = () => {
    if (!confirmSwitchRundown || !socketService.connected) return;
    socketService.emit("select-rundown", confirmSwitchRundown.id);
    setShowRundownSelector(false);
    setConfirmSwitchRundown(null);
  };

  const handleSaveEvent = () => {
    if (!externalActiveRundown || !newEventTitle.trim()) return;

    let calculatedTime = newEventTime;
    const newRundownItems = [...externalActiveRundown.items];

    if (editingEventId) {
      const index = newRundownItems.findIndex((i) => i.id === editingEventId);
      if (index !== -1) {
        newRundownItems[index] = {
          ...newRundownItems[index],
          title: newEventTitle.toUpperCase(),
          description: newEventDescription,
          duration: newEventDuration,
          plannedTime: calculatedTime || newRundownItems[index].plannedTime,
          isParallel: newEventParallel,
        };
      }
    } else {
      if (!calculatedTime && newRundownItems.length > 0) {
        const last = newRundownItems[newRundownItems.length - 1];
        calculatedTime = addTimes(last.plannedTime || "00:00:00", last.duration || "00:00:00");
      } else if (!calculatedTime) {
        calculatedTime = "00:00:00";
      }

      const newItem: RundownItem = {
        id: `RD_${Date.now()}`,
        title: newEventTitle.toUpperCase(),
        description: newEventDescription,
        duration: newEventDuration,
        plannedTime: calculatedTime,
        status: "future",
        isParallel: newEventParallel,
      };
      newRundownItems.push(newItem);
    }

    const recalculatedList = recalculateRundownTimes(newRundownItems);
    socketService.emit("update-rundown", { id: externalActiveRundown.id, items: recalculatedList });

    setIsCreatingEvent(false);
    setEditingEventId(null);
    setNewEventTitle("");
    setNewEventDescription("");
    setNewEventTime("");
    setNewEventDuration("00:05:00");
    setNewEventParallel(false);
  };

  const handleEditEvent = (item: RundownItem) => {
    setNewEventTitle(item.title);
    setNewEventDescription(item.description || "");
    setNewEventTime(item.plannedTime || "");
    setNewEventDuration(item.duration || "00:05:00");
    setNewEventParallel(!!item.isParallel);
    setEditingEventId(item.id);
    setIsCreatingEvent(true);
  };

  const handleDeleteEvent = (id: string) => {
    if (!externalActiveRundown) return;
    const filteredList = externalActiveRundown.items.filter((i) => i.id !== id);
    const recalculatedList = recalculateRundownTimes(filteredList);
    socketService.emit("update-rundown", { id: externalActiveRundown.id, items: recalculatedList });
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget.parentNode as any);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === targetIndex || !externalActiveRundown)
      return;

    const newRundownItems = [...externalActiveRundown.items];
    const [movedItem] = newRundownItems.splice(draggedItemIndex, 1);
    newRundownItems.splice(targetIndex, 0, movedItem);

    const recalculated = recalculateRundownTimes(newRundownItems);
    socketService.emit("update-rundown", { id: externalActiveRundown.id, items: recalculated });

    setDraggedItemIndex(null);
  };

  // Helper to compare time strings (HH:MM:SS or MM:SS)
  const timeToSeconds = (t: string) => {
    if (!t) return 0;
    // Strip any drop-frame timecode frames portion (e.g. "13:03:41;17" -> "13:03:41").
    const parts = t.split(";")[0].split(":").map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0] || 0;
  };

  const secondsToTime = (s: number) => {
    const abs = Math.abs(s);
    const hh = Math.floor(abs / 3600)
      .toString()
      .padStart(2, "0");
    const mm = Math.floor((abs % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const ss = Math.floor(abs % 60)
      .toString()
      .padStart(2, "0");
    const sign = s >= 0 ? "+" : "-";
    return `${sign}${hh !== "00" ? hh + ":" : ""}${mm}:${ss}`;
  };

  // Use props from layout
  const activeRundown = externalActiveRundown || null;
  const allRundowns = externalAllRundowns || [];
  const stories = externalStories || [];
  const participants = externalParticipants || [];
  const activeLoggers = externalActiveLoggers || [];
  const staff = externalStaff || [];

  // Get unique authors from stories to include loggers who might be offline now but have history
  const authorsWithStories = Array.from(
    new Set(stories.map((s) => (s.author || "").trim().toUpperCase()).filter(Boolean)),
  );
  const activeNames = (activeLoggers || []).map((l) => l.name.trim().toUpperCase());

  // Combine both: Currently logged in OR have created stories
  const allPotentialLoggerNames = Array.from(new Set([...authorsWithStories, ...activeNames]));

  const loggerProfiles = allPotentialLoggerNames
    .map((name) => {
      const profile = staff.find((s) => s.name.trim().toUpperCase() === name);
      const isActive = activeNames.includes(name);
      // If they are in the activeLoggers list, they are active.
      // If we found a profile, use it, otherwise create a minimal one.
      return profile
        ? { ...profile, isActive }
        : { id: `logger_${name}`, name, role: "Logger", image: null, isActive };
    })
    // Filter to keep only those who are either explicitly loggers or have stories
    .filter((l) => l.role === "Logger" || authorsWithStories.includes(l.name.trim().toUpperCase()))
    .sort((a, b) => {
      // Sort active ones first, then alphabetical
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return a.name.localeCompare(b.name);
    });

  // Valid Frame-Accurate Timecode
  const { currentDisplayTC: serverTime } = useTimecode();

  const executeGoLive = (item: RundownItem) => {
    if (!activeRundown) return;
    const itemId = item.id;

    const now = new Date();
    const timeStr = now.toLocaleTimeString("en-US", { hour12: false });

    const newItems = activeRundown.items.map((item: RundownItem) => {
      if (item.id === itemId) {
        return { ...item, status: "live" as const, actualStart: timeStr };
      }

      const currentIndex = activeRundown.items.findIndex((i: RundownItem) => i.id === itemId);
      const itemIndex = activeRundown.items.findIndex((i: RundownItem) => i.id === item.id);

      if (itemIndex < currentIndex) {
        if (item.status === "live" && item.actualStart) {
          const parseTime = (t: string) => {
            const [h, m, s] = t.split(":").map(Number);
            const d = new Date();
            d.setHours(h, m, s, 0);
            return d;
          };
          const start = parseTime(item.actualStart);
          const end = now;
          let diff = end.getTime() - start.getTime();
          if (diff < 0) diff += 24 * 60 * 60 * 1000;

          const hh = Math.floor(diff / 3600000)
            .toString()
            .padStart(2, "0");
          const mm = Math.floor((diff % 3600000) / 60000)
            .toString()
            .padStart(2, "0");
          const ss = Math.floor((diff % 60000) / 1000)
            .toString()
            .padStart(2, "0");

          return { ...item, status: "past" as const, actualDuration: `${hh}:${mm}:${ss}` };
        }
        return { ...item, status: "past" as const };
      }

      if (itemIndex === currentIndex + 1) return { ...item, status: "next" as const };
      if (itemIndex > currentIndex + 1) return { ...item, status: "future" as const };

      return item;
    });

    socketService.emit("update-rundown", {
      ...activeRundown,
      items: newItems,
    });
    setConfirmGoLiveItem(null);
  };

  const handleGoLive = (itemId: string) => {
    const item = activeRundown?.items.find((i) => i.id === itemId);
    if (item) {
      setConfirmGoLiveItem(item);
    }
  };

  return (
    <div
      data-testid="dashboard"
      style={{ height: "100%", width: "100%", overflow: "hidden", position: "relative" }}
    >
      {isLoading ? (
        <div
          data-testid="dashboard-loading"
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "#0a0a0a",
            color: "#666",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <i
            className="bx bx-loader-alt bx-spin"
            style={{ fontSize: "4rem", color: "var(--color-role-controller)", opacity: 0.5 }}
          ></i>
          <p
            style={{
              color: "#444",
              fontSize: "1.1rem",
              fontWeight: "bold",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            {t("common.loading")}
          </p>
        </div>
      ) : !activeRundown ? (
        <div
          data-testid="dashboard-no-rundown"
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: "#0a0a0a",
            color: "#666",
          }}
        >
          <div
            data-testid="dashboard-no-rundown-card"
            style={{
              padding: "60px",
              borderRadius: "24px",
              border: "1px solid #1a1a1b",
              background: "rgba(255,255,255,0.01)",
              textAlign: "center",
              maxWidth: "600px",
              backdropFilter: "blur(10px)",
              position: "relative",
            }}
          >
            <div style={{ marginBottom: "20px", opacity: 1 }}>
              <i
                className="bx bx-tv"
                style={{ fontSize: "8rem", color: "var(--color-role-controller)", opacity: 0.2 }}
              ></i>
            </div>
            <h2
              style={{
                color: "#ffb400",
                marginBottom: "10px",
                fontSize: "1.8rem",
                fontWeight: "900",
                letterSpacing: "2px",
              }}
            >
              {t("controller.dashboard.controlMonitor")}
            </h2>
            <p style={{ color: "#444", fontSize: "1.1rem", marginBottom: "30px" }}>
              {t("controller.dashboard.noActiveRundown")}
            </p>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                alignItems: "center",
              }}
            >
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setShowRundownSelector(!showRundownSelector)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    background: "var(--color-input-bg)",
                    padding: "12px 20px",
                    borderRadius: "10px",
                    border: "1px solid var(--color-panel-border)",
                    color: "var(--color-text-primary)",
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: "1rem",
                  }}
                >
                  <i
                    className="bx bx-list-ul"
                    style={{ color: "var(--color-data-rundown)", fontSize: "1.2rem" }}
                  ></i>
                  {t("controller.dashboard.selectRundown")} ▼
                </button>

                {showRundownSelector && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "320px",
                      background: "var(--color-bg-base)",
                      border: "1px solid var(--color-panel-border)",
                      borderRadius: "var(--border-radius-md)",
                      marginTop: "8px",
                      zIndex: 100,
                      boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                      padding: "8px",
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}
                  >
                    {externalAllRundowns && externalAllRundowns.length > 0 ? (
                      externalAllRundowns.map((r) => (
                        <div
                          key={r.id}
                          onClick={() => handleActivateRundown(r.id, r.name)}
                          style={{
                            padding: "12px 15px",
                            borderRadius: "var(--border-radius-sm)",
                            cursor: "pointer",
                            textAlign: "left",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "rgba(255,255,255,0.05)")
                          }
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: "bold",
                                fontSize: "0.85rem",
                                color: "var(--color-text-secondary)",
                              }}
                            >
                              {r.name}
                            </div>
                            <div style={{ fontSize: "0.7rem", opacity: 0.6, marginTop: "2px" }}>
                              {r.date || t("controller.rundownEditor.noDate")}
                            </div>
                          </div>
                          <span
                            style={{
                              fontSize: "0.7rem",
                              color: "var(--color-accent-warning)",
                              fontWeight: "bold",
                              padding: "4px 8px",
                              background: "rgba(245, 158, 11, 0.1)",
                              borderRadius: "4px",
                            }}
                          >
                            {t("controller.dashboard.activate")}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: "15px", textAlign: "center", fontSize: "0.85rem" }}>
                        {t("controller.dashboard.noRundowns")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          data-testid="dashboard-main"
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            padding: "20px",
            background: "#0a0a0a",
            gap: "20px",
            overflow: "hidden",
          }}
        >
          {/* MASTER CLOCK HEADER GRID */}
          <div
            data-testid="master-clock-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(400px, 1.2fr) 1.8fr 1.2fr",
              gap: "20px",
              alignItems: "stretch", // Stretch height to match
              marginBottom: "0",
            }}
          >
            {/* 1. LIVE EVENT ITEM (Aligned with Rundown) */}
            <div
              data-testid="live-event-panel"
              style={{
                background: "rgba(0,0,0,0.4)",
                border: "1px solid var(--color-panel-border)",
                borderRadius: "16px",
                padding: "15px 20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                backdropFilter: "blur(10px)",
                overflow: "hidden",
              }}
            >
              <div
                data-testid="live-event-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "5px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--color-accent-warning)",
                    fontWeight: "bold",
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                  }}
                >
                  {activeRundown.name}
                </div>
                <div
                  style={{
                    fontSize: "0.55rem",
                    color: "#666",
                    fontWeight: "bold",
                    letterSpacing: "1px",
                  }}
                >
                  {t("controller.dashboard.activeRundown")}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                {(() => {
                  const idx = activeRundown.items.findIndex((i: any) => i.status === "live");
                  const item = activeRundown.items[idx];
                  if (!item)
                    return (
                      <div
                        style={{
                          padding: "10px 25px",
                          background: "var(--color-data-event)",
                          borderRadius: "8px",
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "1.8rem",
                          fontWeight: "900",
                          letterSpacing: "2px",
                          boxShadow: "0 8px 25px rgba(56, 189, 248, 0.4)",
                          textTransform: "uppercase",
                          lineHeight: 1,
                        }}
                      >
                        {t("controller.dashboard.stby")}
                      </div>
                    );
                  return (
                    <>
                      <div
                        style={{
                          padding: "10px 15px",
                          background: "var(--color-data-event)",
                          borderRadius: "8px",
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "1.3rem",
                          fontWeight: "900",
                          letterSpacing: "1px",
                          boxShadow: "0 8px 25px rgba(56, 189, 248, 0.4)",
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                      >
                        {idx + 1}
                      </div>
                      <div
                        style={{
                          padding: "10px 20px",
                          background: "var(--color-data-event)",
                          borderRadius: "8px",
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "1.3rem",
                          fontWeight: "900",
                          letterSpacing: "1px",
                          boxShadow: "0 8px 25px rgba(56, 189, 248, 0.4)",
                          textTransform: "uppercase",
                          lineHeight: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                        title={item.title}
                      >
                        {item.title}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* 2. MASTER CLOCKS (Aligned with Middle Column) */}
            <div
              data-testid="master-clocks-panel"
              style={{
                background: "rgba(0,0,0,0.4)",
                border: "1px solid var(--color-panel-border)",
                borderRadius: "16px",
                padding: "15px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center", // Center content
                gap: "40px", // Gap between clocks
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                backdropFilter: "blur(10px)",
              }}
            >
              {/* CURRENT TIME */}
              <div
                data-testid="global-master-tc"
                style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: "#666",
                    fontWeight: "bold",
                    letterSpacing: "2px",
                    marginBottom: "5px",
                  }}
                >
                  {t("controller.dashboard.globalMasterTC")}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "2.6rem",
                    fontWeight: "900",
                    color: "rgba(255, 255, 255, 0.8)",
                    letterSpacing: "4px",
                    textShadow: "0 0 20px rgba(255,255,255,0.15)",
                    lineHeight: 1,
                  }}
                >
                  {serverTime}
                </span>
              </div>

              {/* DELAY INDICATOR */}
              <div
                data-testid="production-offset"
                style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: "#666",
                    fontWeight: "bold",
                    letterSpacing: "2px",
                    marginBottom: "5px",
                  }}
                >
                  {t("controller.dashboard.productionOffset")}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "2.6rem",
                    fontWeight: "900",
                    color:
                      (activeRundown.items.find((i: any) => i.status === "live")?.plannedTime ||
                        serverTime) > serverTime
                        ? "var(--color-accent-success)"
                        : "var(--color-accent-danger)",
                    letterSpacing: "4px",
                    lineHeight: 1,
                    textShadow:
                      (activeRundown.items.find((i: any) => i.status === "live")?.plannedTime ||
                        serverTime) > serverTime
                        ? "0 0 20px rgba(0, 208, 132, 0.2)"
                        : "0 0 20px rgba(247, 45, 96, 0.2)",
                  }}
                >
                  {(() => {
                    const liveItem = activeRundown.items.find((i: any) => i.status === "live");
                    if (!liveItem || !liveItem.plannedTime) return "+00:00:00";
                    const nowSec = timeToSeconds(serverTime);
                    const planSec = timeToSeconds(liveItem.plannedTime);
                    const diff = nowSec - planSec;
                    const absDiff = Math.abs(diff);
                    const sign = diff >= 0 ? "+" : "-";
                    const h = Math.floor(absDiff / 3600)
                      .toString()
                      .padStart(2, "0");
                    const m = Math.floor((absDiff % 3600) / 60)
                      .toString()
                      .padStart(2, "0");
                    const s = (absDiff % 60).toString().padStart(2, "0");
                    return `${sign}${h}:${m}:${s}`;
                  })()}
                </span>
              </div>
            </div>

            {/* 3. SYSTEM STATUS & TOTALS (Aligned with Right Column) */}
            <div
              data-testid="stats-panel"
              style={{
                background: "rgba(0,0,0,0.4)",
                border: "1px solid var(--color-panel-border)",
                borderRadius: "16px",
                padding: "10px 15px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                backdropFilter: "blur(10px)",
              }}
            >
              <StatsWidget
                stories={stories}
                eventsCount={eventsCount}
                transcriptsCount={transcriptsCount}
              />
            </div>
          </div>

          <div
            data-testid="dashboard-columns"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(400px, 1.2fr) 1.8fr 1.2fr",
              gap: "20px",
              flex: 1,
              overflow: "hidden",
            }}
          >
            {/* Column 1: Rundown List */}
            <div
              data-testid="rundown-column"
              style={{
                background: "var(--color-panel-bg)",
                borderRadius: "var(--border-radius-lg)",
                border: "1px solid var(--color-panel-border)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "var(--shadow-card)",
              }}
            >
              <div
                data-testid="rundown-header"
                style={{
                  padding: "15px",
                  borderBottom: "1px solid var(--color-panel-border)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "var(--color-bg-base)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <i
                    className="bx bx-list-ul"
                    style={{ fontSize: "1.2rem", color: "var(--color-data-rundown)" }}
                  ></i>
                  <span
                    style={{
                      fontWeight: "900",
                      letterSpacing: "2px",
                      fontSize: "1rem",
                      color: "#ffffff",
                    }}
                  >
                    {t("controller.dashboard.rundown")}
                  </span>
                </div>

                {/* RUNDOWN SWITCHER / EVENT CREATOR */}
                <div
                  ref={rundownDropdownRef}
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <button
                    data-testid="add-event-btn"
                    onClick={() => {
                      setShowRundownSelector(false);
                      setIsCreatingEvent(true);
                      setEditingEventId(null);
                      setNewEventTitle("");
                      setNewEventDescription("");
                      setNewEventTime("");
                      setNewEventDuration("00:05:00");
                      setNewEventParallel(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "var(--color-data-event)",
                      padding: "6px 12px",
                      borderRadius: "8px",
                      border: "none",
                      color: "#000",
                      fontWeight: "bold",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      boxShadow: "0 4px 10px rgba(56, 189, 248, 0.2)",
                    }}
                    title={t("controller.dashboard.addNewEvent")}
                  >
                    {t("controller.dashboard.addEvent")}
                  </button>
                  <button
                    data-testid="change-rundown-btn"
                    onClick={() => setShowRundownSelector(!showRundownSelector)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      background: "var(--color-input-bg)",
                      padding: "6px 12px",
                      borderRadius: "8px",
                      border: "1px solid var(--color-panel-border)",
                      color: "var(--color-text-primary)",
                      fontWeight: "bold",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                    }}
                    title={t("controller.dashboard.changeRundown")}
                  >
                    <i
                      className="bx bx-list-ul"
                      style={{ color: "var(--color-data-rundown)", fontSize: "1rem" }}
                    ></i>
                    {t("controller.dashboard.changeRundown")} ▼
                  </button>

                  {showRundownSelector && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        width: "320px",
                        background: "var(--color-bg-base)",
                        border: "1px solid var(--color-panel-border)",
                        borderRadius: "var(--border-radius-md)",
                        marginTop: "8px",
                        zIndex: 100,
                        boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                        padding: "8px",
                        maxHeight: "320px",
                        overflowY: "auto",
                      }}
                    >
                      {allRundowns.length > 0 ? (
                        [...allRundowns]
                          .sort((a, b) => {
                            const ta = a.scheduledAt
                              ? new Date(a.scheduledAt).getTime()
                              : a.date
                                ? new Date(a.date).getTime()
                                : Number.POSITIVE_INFINITY;
                            const tb = b.scheduledAt
                              ? new Date(b.scheduledAt).getTime()
                              : b.date
                                ? new Date(b.date).getTime()
                                : Number.POSITIVE_INFINITY;
                            if (ta !== tb) return ta - tb;
                            return a.name.localeCompare(b.name, undefined, { numeric: true });
                          })
                          .map((r) => (
                            <div
                              key={r.id}
                              onClick={() => handleActivateRundown(r.id, r.name)}
                              style={{
                                padding: "10px 12px",
                                borderRadius: "var(--border-radius-sm)",
                                cursor: "pointer",
                                textAlign: "left",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid rgba(255,255,255,0.05)",
                                background:
                                  activeRundown?.id === r.id
                                    ? "rgba(56, 189, 248, 0.1)"
                                    : "transparent",
                                borderLeft:
                                  activeRundown?.id === r.id
                                    ? "3px solid var(--color-data-event)"
                                    : "3px solid transparent",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background =
                                  activeRundown?.id === r.id
                                    ? "rgba(56, 189, 248, 0.1)"
                                    : "rgba(255,255,255,0.05)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background =
                                  activeRundown?.id === r.id
                                    ? "rgba(56, 189, 248, 0.1)"
                                    : "transparent")
                              }
                            >
                              <div>
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "0.8rem",
                                    color:
                                      activeRundown?.id === r.id
                                        ? "#fff"
                                        : "var(--color-text-secondary)",
                                  }}
                                >
                                  {r.name}
                                </div>
                                <div style={{ fontSize: "0.7rem", opacity: 0.6, marginTop: "2px" }}>
                                  {r.date || t("controller.rundownEditor.noDate")}
                                </div>
                              </div>
                              {activeRundown?.id !== r.id ? (
                                <span
                                  style={{
                                    fontSize: "0.6rem",
                                    color: "var(--color-accent-warning)",
                                    fontWeight: "bold",
                                    padding: "2px 6px",
                                    border: "1px solid var(--color-accent-warning)",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {t("controller.dashboard.activate")}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    fontSize: "0.6rem",
                                    color: "var(--color-data-event)",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {t("controller.dashboard.live")}
                                </span>
                              )}
                            </div>
                          ))
                      ) : (
                        <div style={{ padding: "15px", textAlign: "center", fontSize: "0.85rem" }}>
                          {t("controller.dashboard.noRundowns")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div data-testid="rundown-items-list" style={{ flex: 1, overflowY: "auto" }}>
                {activeRundown.items.map((item: RundownItem, idx: number) => {
                  const showParallel = item.isParallel || activeRundown.items[idx + 1]?.isParallel;
                  return (
                    <div
                      key={item.id}
                      data-testid={`rundown-item-${item.id}`}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, idx)}
                      onMouseEnter={() => setHoveredRundownItemId(item.id)}
                      onMouseLeave={() => setHoveredRundownItemId(null)}
                      style={{
                        padding: "12px 15px",
                        borderBottom: "1px solid var(--color-panel-border)",
                        background:
                          draggedItemIndex === idx
                            ? "var(--color-bg-mesh)"
                            : item.status === "live"
                              ? "rgba(56, 189, 248, 0.1)"
                              : "var(--color-bg-base)",
                        display: "grid",
                        gridTemplateColumns: "min-content 1fr min-content min-content",
                        gap: "12px",
                        alignItems: "center",
                        marginBottom: "8px",
                        borderRadius: "var(--border-radius-md)",
                        border:
                          draggedItemIndex === idx
                            ? "1px dashed var(--color-text-muted)"
                            : item.status === "live"
                              ? "1px solid var(--color-data-event)"
                              : "1px solid var(--color-panel-border)",
                        position: "relative",
                        opacity: draggedItemIndex === idx ? 0.4 : 1,
                        cursor: "grab",
                        zIndex: hoveredRundownItemId === item.id ? 100 : 1,
                      }}
                    >
                      <div
                        style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                      >
                        <span
                          style={{
                            color:
                              item.status === "live"
                                ? "var(--color-data-event)"
                                : item.status === "past"
                                  ? "rgba(255, 255, 255, 0.25)"
                                  : "var(--color-data-event)",
                            fontSize: "1rem",
                            fontWeight: "900",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {idx + 1}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          overflow: "hidden",
                        }}
                      >
                        {showParallel && (
                          <span
                            style={{
                              color:
                                item.status === "past"
                                  ? "rgba(56, 189, 248, 0.3)"
                                  : "var(--color-accent-primary)",
                              fontWeight: "bold",
                              fontSize: "1.2rem",
                              lineHeight: 1,
                            }}
                          >
                            ║
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: "1rem",
                            fontWeight: "bold",
                            color:
                              item.status === "live"
                                ? "rgba(255, 255, 255, 0.8)"
                                : item.status === "past"
                                  ? "rgba(255, 255, 255, 0.3)"
                                  : "var(--color-text-primary)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.title}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          fontSize: "0.8rem",
                          fontFamily: "var(--font-mono)",
                          minWidth: "80px",
                        }}
                      >
                        <div style={{ display: "flex", gap: "8px" }}>
                          <span
                            style={{
                              color:
                                item.status === "past"
                                  ? "rgba(255, 180, 0, 0.4)"
                                  : "var(--color-accent-warning)",
                              fontWeight: "bold",
                            }}
                          >
                            {item.plannedTime || "--:--:--"}
                          </span>
                          <span
                            style={{
                              color:
                                item.status === "past"
                                  ? "rgba(160, 160, 160, 0.4)"
                                  : "var(--color-text-secondary)",
                              fontSize: "0.75rem",
                            }}
                          >
                            ({item.duration || "--"})
                          </span>
                        </div>
                        {item.status === "past" && (
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              marginTop: "2px",
                              borderTop: "1px solid #333",
                              paddingTop: "2px",
                            }}
                          >
                            <span
                              style={{
                                color:
                                  item.actualStart && item.plannedTime
                                    ? timeToSeconds(item.actualStart) <=
                                      timeToSeconds(item.plannedTime)
                                      ? "var(--color-accent-success)"
                                      : "var(--color-accent-danger)"
                                    : "var(--color-accent-success)",
                                fontWeight: "bold",
                                fontSize: "0.75rem",
                              }}
                            >
                              {item.actualStart || "--:--:--"}
                            </span>
                            <span
                              style={{
                                color:
                                  item.actualDuration && item.duration
                                    ? timeToSeconds(item.actualDuration) <=
                                      timeToSeconds(item.duration)
                                      ? "var(--color-accent-success)"
                                      : "var(--color-accent-danger)"
                                    : "#888",
                                fontSize: "0.7rem",
                              }}
                            >
                              ({item.actualDuration || "--"})
                            </span>
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {item.status !== "past" && item.status !== "live" && (
                          <button
                            data-testid={`rundown-item-edit-btn-${item.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditEvent(item);
                            }}
                            style={{
                              background: "transparent",
                              color: "var(--color-accent-primary)",
                              border: "none",
                              padding: "6px",
                              cursor: "pointer",
                              opacity: 0.6,
                              transition: "opacity 0.2s",
                              display: "flex",
                              alignItems: "center",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
                            title={t("controller.dashboard.editEvent")}
                          >
                            <i className="bx bx-pencil" style={{ fontSize: "1.2rem" }}></i>
                          </button>
                        )}
                        {item.status !== "past" && item.status !== "live" && (
                          <button
                            data-testid={`rundown-item-delete-btn-${item.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteEvent(item.id);
                            }}
                            style={{
                              background: "transparent",
                              color: "var(--color-accent-danger)",
                              border: "none",
                              padding: "6px",
                              cursor: "pointer",
                              opacity: 0.6,
                              transition: "opacity 0.2s",
                              display: "flex",
                              alignItems: "center",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
                            title={t("controller.dashboard.deleteEvent")}
                          >
                            <i className="bx bx-trash" style={{ fontSize: "1.2rem" }}></i>
                          </button>
                        )}
                        {item.status !== "past" && item.status !== "live" && (
                          <button
                            data-testid={`rundown-item-go-btn-${item.id}`}
                            onClick={() => handleGoLive(item.id)}
                            style={{
                              background:
                                item.status === "next"
                                  ? "var(--color-accent-danger)"
                                  : "var(--color-bg-mesh)",
                              color:
                                item.status === "next" ? "#fff" : "var(--color-text-secondary)",
                              border: "1px solid var(--color-panel-border)",
                              padding: "6px 12px",
                              borderRadius: "4px",
                              fontWeight: "bold",
                              fontSize: "0.7rem",
                              cursor: "pointer",
                            }}
                          >
                            GO
                          </button>
                        )}
                        {item.status === "live" && (
                          <span
                            style={{
                              color: "var(--color-data-event)",
                              fontWeight: "bold",
                              fontSize: "0.7rem",
                              border: "1px solid var(--color-data-event)",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              background: "rgba(56, 189, 248, 0.1)",
                            }}
                          >
                            {t("controller.dashboard.live")}
                          </span>
                        )}
                        {item.status === "past" && item.actualDuration && item.duration && (
                          <span
                            style={{
                              color:
                                timeToSeconds(item.actualDuration) <= timeToSeconds(item.duration)
                                  ? "var(--color-accent-success)"
                                  : "var(--color-accent-danger)",
                              fontWeight: "bold",
                              fontSize: "0.7rem",
                              fontFamily: "var(--font-mono)",
                              background:
                                timeToSeconds(item.actualDuration) <= timeToSeconds(item.duration)
                                  ? "rgba(0, 208, 132, 0.1)"
                                  : "rgba(247, 45, 96, 0.1)",
                              padding: "2px 8px",
                              borderRadius: "6px",
                              border: `1px solid ${timeToSeconds(item.actualDuration) <= timeToSeconds(item.duration) ? "var(--color-accent-success)" : "var(--color-accent-danger)"}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {secondsToTime(
                              timeToSeconds(item.duration) - timeToSeconds(item.actualDuration),
                            )}
                          </span>
                        )}
                      </div>

                      {/* Hover Popup */}
                      {hoveredRundownItemId === item.id && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: "10px",
                            zIndex: 9999,
                            background: "#1a1a1b",
                            border: "1px solid var(--color-accent-danger)",
                            borderRadius: "var(--border-radius-md)",
                            padding: "16px",
                            color: "rgba(255, 255, 255, 0.8)",
                            width: "fit-content",
                            maxWidth: "400px",
                            whiteSpace: "pre-wrap",
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                            boxShadow: "0 10px 40px rgba(0,0,0,0.8)",
                            marginTop: "4px",
                            fontSize: "0.85rem",
                            lineHeight: "1.4",
                            pointerEvents: "none",
                            minWidth: "250px",
                          }}
                        >
                          <div
                            style={{
                              color: "rgba(255, 255, 255, 0.8)",
                              fontWeight: "bold",
                              fontSize: "1rem",
                              marginBottom: "6px",
                              borderBottom: "1px solid rgba(255,255,255,0.1)",
                              paddingBottom: "4px",
                            }}
                          >
                            {item.title}
                          </div>
                          <div
                            style={{
                              fontSize: "0.65rem",
                              color: "var(--color-accent-danger)",
                              marginBottom: "2px",
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                            }}
                          >
                            {t("controller.rundownEditor.description")}
                          </div>
                          <div style={{ color: "#aaa", fontSize: "0.8rem" }}>
                            {item.description || t("controller.dashboard.noDescription")}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Column 2: LOGGER HISTORY */}
            <div
              data-testid="history-column"
              style={{
                background: "var(--color-panel-bg)",
                borderRadius: "var(--border-radius-lg)",
                border: "1px solid var(--color-panel-border)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "var(--shadow-card)",
              }}
            >
              {/* Header: Flush & Full Width */}
              <div
                data-testid="history-header"
                style={{
                  padding: "15px",
                  borderBottom: "1px solid var(--color-panel-border)",
                  background: "var(--color-bg-base)",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <i
                  className="bx bx-history"
                  style={{ fontSize: "1.2rem", color: "var(--color-data-log)" }}
                ></i>
                <span
                  style={{
                    fontWeight: "900",
                    letterSpacing: "2px",
                    fontSize: "1rem",
                    color: "#ffffff",
                  }}
                >
                  {t("controller.dashboard.history")}
                </span>
              </div>

              {/* Profiles & Controls Sub-Header */}
              <div
                data-testid="history-loggers-panel"
                style={{
                  padding: "10px 15px 15px 15px",
                  borderBottom: "1px solid var(--color-panel-border)",
                  background: "var(--color-bg-base)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  flexShrink: 0,
                }}
              >
                {/* Controls Row */}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    data-testid="view-all-loggers-btn"
                    onClick={() => setFocusLogger(null)}
                    style={{
                      background: !focusLogger ? "var(--color-accent-success)" : "#222",
                      color: !focusLogger ? "#000" : "#888",
                      border: "none",
                      fontSize: "0.6rem",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      letterSpacing: "1px",
                    }}
                  >
                    {t("controller.dashboard.viewAll")}
                  </button>
                </div>

                <div
                  data-testid="logger-profiles-list"
                  style={{
                    display: "flex",
                    gap: "15px",
                    overflowX: "auto",
                    padding: "5px 0 10px 0",
                    scrollbarWidth: "none",
                  }}
                >
                  {loggerProfiles.map((l) => {
                    const firstName = l.name.split(" ")[0];
                    const isSelected = focusLogger === l.name;
                    const isOnline = l.isActive;

                    return (
                      <div
                        key={l.id}
                        data-testid={`logger-profile-${l.id}`}
                        onClick={() => setFocusLogger(isSelected ? null : l.name)}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "5px",
                          cursor: "pointer",
                          minWidth: "60px",
                          transition: "all 0.2s",
                          opacity: !focusLogger || isSelected ? 1 : 0.4,
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            width: "45px",
                            height: "45px",
                            aspectRatio: "1 / 1",
                            flexShrink: 0,
                            borderRadius: "50%",
                            border: isSelected
                              ? "2px solid var(--color-accent-success)"
                              : "1px solid var(--color-panel-border)",
                            padding: "2px",
                            background: isOnline
                              ? "rgba(0, 208, 132, 0.05)"
                              : "rgba(255,255,255,0.02)",
                            transition: "all 0.2s",
                            boxShadow: isSelected ? "0 0 15px rgba(0, 208, 132, 0.4)" : "none",
                            position: "relative",
                          }}
                        >
                          {l.image ? (
                            <img
                              src={l.image}
                              alt={l.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                objectFit: "cover",
                                filter: isOnline ? "none" : "grayscale(0.8)",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                background: "#333",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.8rem",
                                fontWeight: "bold",
                                color: isOnline ? "#fff" : "#666",
                              }}
                            >
                              {l.name[0]}
                            </div>
                          )}

                          {/* Online Status Dot */}
                          <div
                            style={{
                              position: "absolute",
                              bottom: "0",
                              right: "0",
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              background: isOnline ? "#00d084" : "#444",
                              border: "2px solid #0a0a0a",
                              boxShadow: isOnline ? "0 0 5px rgba(0, 208, 132, 0.5)" : "none",
                            }}
                          ></div>
                        </div>
                        <span
                          style={{
                            fontSize: "0.6rem",
                            fontWeight: "900",
                            color: isSelected
                              ? "var(--color-accent-success)"
                              : isOnline
                                ? "#eee"
                                : "#555",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            textAlign: "center",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {firstName}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ flex: 1, overflow: "hidden", padding: "0" }}>
                <React.Suspense fallback={null}>
                  <DataColumn
                    stories={stories}
                    rundownItems={activeRundown?.items || []}
                    loggerName={focusLogger || undefined}
                    hideTitle={true}
                    hideSweeper={true}
                    collapsed={false}
                    participants={participants}
                    onFetchStoryLogs={onFetchStoryLogs}
                  />
                </React.Suspense>
              </div>
            </div>

            {/* Column 3: SPLIT (STREAM + CHAT) */}
            <div
              data-testid="right-column"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                height: "100%",
                overflow: "hidden",
              }}
            >
              {/* Top Half: STREAM */}
              <StreamFeed externalStories={stories} externalParticipants={participants} />

              {/* Bottom Half: CHAT */}
              <div
                data-testid="chat-panel"
                style={{
                  flex: 1,
                  minHeight: 0,
                  background: "var(--color-panel-bg)",
                  borderRadius: "var(--border-radius-lg)",
                  border: "1px solid var(--color-panel-border)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  boxShadow: "var(--shadow-card)",
                }}
              >
                <ChatSidebar embedded={true} />
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmGoLiveItem && (
        <CustomDialog
          isOpen={true}
          title={t("controller.dashboard.confirmGoLive")}
          message={t("controller.dashboard.confirmGoLiveMsg", { title: confirmGoLiveItem.title })}
          type="confirm"
          onConfirm={() => executeGoLive(confirmGoLiveItem)}
          onCancel={() => setConfirmGoLiveItem(null)}
          confirmText={t("controller.dashboard.goLive")}
          cancelText={t("controller.dashboard.cancel")}
        />
      )}

      {confirmSwitchRundown && (
        <CustomDialog
          isOpen={true}
          title={t("controller.dashboard.confirmRundownSwitch")}
          message={t("controller.dashboard.confirmRundownSwitchMsg", {
            name: confirmSwitchRundown.name,
          })}
          type="confirm"
          onConfirm={executeSwitchRundown}
          onCancel={() => setConfirmSwitchRundown(null)}
          confirmText={t("controller.dashboard.yesSwitchRundown")}
          cancelText={t("controller.dashboard.cancel")}
        />
      )}

      {isCreatingEvent && (
        <div
          data-testid="event-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            animation: "fadeIn 0.2s ease-out",
          }}
        >
          <div
            data-testid="event-modal"
            style={{
              background: "#1a1a1b",
              border: "1px solid #333",
              borderRadius: "16px",
              padding: "30px",
              width: "100%",
              maxWidth: "500px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <h2
                style={{
                  margin: 0,
                  color: "var(--color-accent-warning)",
                  fontSize: "1.2rem",
                  fontWeight: "900",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                }}
              >
                {editingEventId
                  ? t("controller.dashboard.editEventTitle")
                  : t("controller.dashboard.addNewEventTitle")}
              </h2>
              <p style={{ margin: 0, color: "#ccc", fontSize: "0.95rem", lineHeight: "1.5" }}>
                {editingEventId
                  ? t("controller.dashboard.updateDetails")
                  : t("controller.dashboard.fillDetails")}
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.75rem",
                    color: "var(--color-text-muted)",
                    marginBottom: "5px",
                  }}
                >
                  {t("controller.rundownEditor.eventTitle")}{" "}
                  <span style={{ color: "var(--color-accent-danger)" }}>*</span>
                </label>
                <input
                  data-testid="event-title-input"
                  autoFocus
                  value={newEventTitle}
                  onChange={(e) => setNewEventTitle(e.target.value.toUpperCase())}
                  placeholder={t("controller.rundownEditor.eventTitlePlaceholder")}
                  style={{
                    width: "100%",
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "var(--color-text-primary)",
                    padding: "12px 15px",
                    borderRadius: "var(--border-radius-sm)",
                    outline: "none",
                    fontSize: "0.95rem",
                    fontWeight: "bold",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.75rem",
                    color: "var(--color-text-muted)",
                    marginBottom: "5px",
                  }}
                >
                  {t("controller.dashboard.descriptionOptional")}
                </label>
                <textarea
                  data-testid="event-description-input"
                  value={newEventDescription}
                  onChange={(e) => setNewEventDescription(e.target.value)}
                  placeholder={t("controller.dashboard.descriptionPlaceholder")}
                  style={{
                    width: "100%",
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "var(--color-text-primary)",
                    padding: "12px 15px",
                    borderRadius: "var(--border-radius-sm)",
                    outline: "none",
                    fontSize: "0.9rem",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  background: "rgba(0,0,0,0.2)",
                  padding: "15px",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.05)",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "var(--color-text-muted)",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    {t("controller.dashboard.plannedTime")}
                  </label>
                  <div
                    style={{
                      opacity:
                        !externalActiveRundown ||
                        externalActiveRundown.items.length === 0 ||
                        (editingEventId && externalActiveRundown.items[0]?.id === editingEventId)
                          ? 1
                          : 0.5,
                      pointerEvents:
                        !externalActiveRundown ||
                        externalActiveRundown.items.length === 0 ||
                        (editingEventId && externalActiveRundown.items[0]?.id === editingEventId)
                          ? "auto"
                          : "none",
                    }}
                  >
                    <TimeControl value={newEventTime} onChange={setNewEventTime} />
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "var(--color-text-muted)",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    {t("controller.rundownEditor.duration")}{" "}
                    <span style={{ color: "var(--color-accent-danger)" }}>*</span>
                  </label>
                  <TimeControl value={newEventDuration} onChange={setNewEventDuration} />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "5px" }}>
                <label
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--color-text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  <input
                    data-testid="event-parallel-checkbox"
                    type="checkbox"
                    checked={newEventParallel}
                    onChange={(e) => setNewEventParallel(e.target.checked)}
                    style={{ accentColor: "var(--color-data-rundown)", transform: "scale(1.2)" }}
                  />
                  <span>{t("controller.dashboard.runInParallel")}</span>
                </label>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "10px",
              }}
            >
              <button
                data-testid="event-cancel-btn"
                onClick={() => {
                  setIsCreatingEvent(false);
                  setEditingEventId(null);
                  setNewEventTitle("");
                  setNewEventDescription("");
                  setNewEventTime("");
                  setNewEventDuration("00:05:00");
                  setNewEventParallel(false);
                }}
                style={{
                  background: "transparent",
                  border: "1px solid #444",
                  borderRadius: "8px",
                  color: "#888",
                  padding: "10px 20px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {t("controller.dashboard.cancel")}
              </button>
              <button
                data-testid="event-save-btn"
                onClick={handleSaveEvent}
                disabled={!newEventTitle.trim() || !newEventDuration.trim()}
                style={{
                  background: "var(--color-accent-success)",
                  border: "none",
                  borderRadius: "8px",
                  color: "#000",
                  padding: "10px 25px",
                  fontWeight: "bold",
                  cursor:
                    !newEventTitle.trim() || !newEventDuration.trim() ? "not-allowed" : "pointer",
                  opacity: !newEventTitle.trim() || !newEventDuration.trim() ? 0.5 : 1,
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                }}
              >
                {editingEventId
                  ? t("controller.dashboard.updateEvent")
                  : t("controller.dashboard.addEvent")}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
                    @keyframes blink { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
                    ::-webkit-scrollbar { width: 5px; }
                    ::-webkit-scrollbar-track { background: transparent; }
                    ::-webkit-scrollbar-thumb { background: #1a1a1b; borderRadius: 10px; }
                `}</style>
    </div>
  );
};
